<template>
  <div class="px-4 py-6">
    <!-- Header -->
    <div class="mb-4 flex items-center justify-between">
      <h3 class="text-theme-gray-5 text-lg font-bold">Summary</h3>

      <span
        v-if="userSearch"
        class="pill"
        :class="userSearch.type === 'distance'
          ? 'pill-emerald'
          : 'pill-sky'"
      >
        {{ userSearch.type }}
      </span>
    </div>

    <div v-if="userSearch" class="space-y-4">
      <!-- Card: Vehicle (MOVED TO TOP) -->
      <div
        v-if="selectedCar && currentStep?.uri === 'contact-details'"
        class="card !bg-emerald-50/50 !border-emerald-200"
      >
        <div class="flex items-start justify-between gap-4">
          <div class="kv !mb-0">
            <div class="kv-label">Vehicle selected</div>
            <div class="kv-value text-lg">
              {{ (Number(selectedCar.price || 0) <= 0) ? (selectedCar.type?.name || selectedCar.name) : selectedCar.name }}
            </div>
          </div>

          <div
            v-if="selectedCar.price && selectedCar.price > 0 && !couponDiscount"
            class="rounded-full bg-emerald-500 px-4 py-1.5 text-base font-bold text-[#0a0d0f] shadow-sm"
          >
            ${{ selectedCar.price.toFixed(2) }}
          </div>

          <div
            v-if="couponDiscount && newTotal !== undefined"
            class="flex flex-col items-end text-sm"
          >
            <div class="line-through text-slate-500">${{ (selectedCar.price ?? 0).toFixed(2) }}</div>
            <div class="rounded-full bg-emerald-500 px-4 py-1 flex items-center font-bold text-[#0a0d0f] shadow-sm">
                ${{ newTotal.toFixed(2) }}
            </div>
            <div class="text-xs text-emerald-600 font-bold mt-1">
                Discount: -${{ couponDiscount.toFixed(2) }}
            </div>
          </div>
        </div>
      </div>

      <!-- Card: Where / When -->
      <div class="card space-y-6">
        <!-- Date / Time -->
        <div class="kv">
          <div class="kv-label">Pickup date & time</div>
          <div class="kv-value text-base">
            {{ userSearch.pickUp.date }}, {{ userSearch.pickUp.time }}
          </div>
        </div>

        <!-- Pickup -->
        <div class="kv">
          <div class="kv-label">Pickup location</div>
          <button
            @click="handlePickUpClick"
            class="kv-value text-base text-left hover:underline"
          >
            {{ userSearch.pickUp.location.name }}
          </button>
        </div>

        <!-- Dropoff (distance only) -->
        <div v-if="userSearch.type === 'distance'" class="kv">
          <div class="kv-label">Drop-off location</div>
          <div class="kv-value text-base">
            {{ userSearch.dropOff.location.name }}
          </div>
        </div>

        <!-- Distance & Duration text (computed from Map) -->
        <div v-if="userSearch.type === 'distance' && distance !== '—'" class="kv">
          <div class="kv-label">Distance & Duration</div>
          <div class="kv-value text-base text-emerald-600 font-bold">
            {{ distance }} ({{ time }})
          </div>
        </div>

        <!-- Map (distance only) -->
        <div
          v-if="userSearch.type === 'distance' && userSearch.dropOff?.location?.id"
          class="mt-5 overflow-hidden rounded-xl border border-theme-gray-9"
        >
          <GoogleMap
            :key="mapKey"
            :origin="userSearch.pickUp.location.id"
            :destination="userSearch.dropOff.location.id"
            :driveType="userSearch.type"
            @distance="handleMapData"
            @time="handleMapData"
          />
        </div>

        <!-- Chips -->
        <div class="mt-5 flex flex-wrap items-center gap-2">
          <template v-if="userSearch.type === 'distance'">
            <span class="chip">{{ distance }}</span>
            <span class="chip">{{ time }}</span>
          </template>
          <template v-else>
            <span class="chip">{{ (userSearch.hours ?? 0) / 10 }} day(s)</span>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { retrieveUserSearch } from '@/services/search-service'
import { UserSearch, Car as CarData } from '@/types'
import { getSessionPrefix } from '@/utils/common'
import GoogleMap from '@/components/GoogleMap.vue'

const props = defineProps<{ 
  currentStep?: any,
  couponDiscount?: number,
  newTotal?: number
}>()

const emit = defineEmits<{
  (e: 'distance-arrived', value: number): void
}>()

const userSearch = ref<UserSearch | null>(null)
const selectedCar = ref<CarData | null>(null)

const distance = ref<string>('—')
const time = ref<string>('—')
const mapKey = ref<number>(0)

const getCategoryImage = (typeName: string) => {
  const name = (typeName || '').toLowerCase()
  if (name.includes('premium')) return '/assets/cars/e-class.png'
  if (name.includes('luxury')) return '/assets/cars/s-class.png'
  if (name.includes('van')) return '/assets/cars/v-class.png'
  if (name.includes('suv')) return '/assets/cars/suv.png'
  
  return '/assets/nocarfound.jpeg'
}

function handleMapData(value: string, type: string) {
  if (type === 'distance') {
     distance.value = value || '—'
     // Extract numeric value from string like "20.7 km"
     const numericValue = parseFloat((value || '0').replace(/[^\d.]/g, ''))
     emit('distance-arrived', numericValue)
  }
  else if (type === 'time') time.value = value || '—'
}

function handlePickUpClick() {
  const locationName = userSearch.value?.pickUp.location.name as string
  const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(locationName)}`
  window.open(url, '_blank')
}

onMounted(() => {
  userSearch.value = retrieveUserSearch()
  selectedCar.value = retrieveSelectedVehicle()

  if (userSearch.value?.type === 'distance') {
    // force map to remount so it recalculates for the *current* search
    mapKey.value++

    // Calculate fallback Haversine distance immediately (self-healing if Google Maps fails)
    try {
      const pLoc = userSearch.value?.pickUp?.location
      const dLoc = userSearch.value?.dropOff?.location
      if (pLoc?.coordinates?.lat && pLoc?.coordinates?.lng && dLoc?.coordinates?.lat && dLoc?.coordinates?.lng) {
        const lat1 = Number(pLoc.coordinates.lat)
        const lon1 = Number(pLoc.coordinates.lng)
        const lat2 = Number(dLoc.coordinates.lat)
        const lon2 = Number(dLoc.coordinates.lng)
        
        const R = 6371 // Radius of the earth in km
        const dLat = (lat2 - lat1) * Math.PI / 180
        const dLon = (lon2 - lon1) * Math.PI / 180
        const a = 
           Math.sin(dLat/2) * Math.sin(dLat/2) +
           Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
           Math.sin(dLon/2) * Math.sin(dLon/2)
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a))
        const d = R * c // Distance in km
        const estimatedKm = parseFloat((d * 1.3).toFixed(1)) // Multiply by 1.3 routing factor

        if (estimatedKm > 0) {
          distance.value = `${estimatedKm} km`
          time.value = `${Math.ceil(estimatedKm * 1.5)} mins`
          emit('distance-arrived', estimatedKm)
        }
      }
    } catch (e) {
      console.log('Error calculating fallback distance:', e)
    }
  } else {
    distance.value = '—'
    time.value = '—'
  }
})

function retrieveSelectedVehicle(): CarData | null {
  const prefix = getSessionPrefix()
  const raw = localStorage.getItem(`${prefix}_selected_car`)
  return raw ? JSON.parse(raw) : null
}
</script>

<style scoped>
/* Card wrapper */
.card{
  @apply rounded-2xl border border-theme-gray-9 bg-white/70 backdrop-blur p-4;
}

/* Label/value block */
.kv{ @apply mb-1; }
.kv-label{
  @apply text-theme-gray-2 text-[11px] uppercase font-semibold tracking-wide;
}
.kv-value{
  @apply mt-1 text-theme-gray-5 font-semibold;
}

/* Chips */
.chip{
  @apply rounded-full border border-theme-gray-9 bg-theme-gray-1 px-3 py-1 text-[12px] font-semibold text-theme-gray-5;
}

/* Badges in the header */
.pill{
  @apply rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-wide;
}
.pill-emerald{ @apply bg-emerald-100 text-emerald-700; }
.pill-sky{ @apply bg-sky-100 text-sky-700; }
</style>